import { useEffect, useRef, useState } from "react";

/**
 * useScrollReveal
 *
 * Lightweight intersection-observer based reveal hook.
 * Attach the returned ref to an element and it will get `data-revealed="true"`
 * once it scrolls into view. CSS handles the animation via the
 * [data-reveal] / [data-revealed] selectors in landing.css.
 *
 * Respects prefers-reduced-motion automatically (instantly marks as revealed).
 */
export default function useScrollReveal({
  threshold = 0.15,
  rootMargin = "0px 0px -8% 0px",
  once = true,
} = {}) {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // If the user prefers reduced motion, reveal immediately.
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) {
      setRevealed(true);
      return;
    }

    // Older browsers — reveal immediately as a graceful fallback.
    if (typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setRevealed(true);
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            setRevealed(false);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return [ref, revealed];
}
